import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import type {
  LoginRequest,
  TokenResponse,
  User,
  Team,
  TeamComment,
  Game,
  GameListParams,
  Referee,
  RefereeShift,
  Field,
  FieldGroup,
  EventSubmitRequest,
  EventSubmitResponse,
  BlockData,
  LeagueData,
  GenerateTournamentRequest,
  GenerateTournamentResponse,
  TournamentStats,
  DisqualifyResponse,
  UndisqualifyResponse,
  ClearTournamentResponse,
  ClearUsersResponse,
  ClearFieldsResponse,
  ClearAuditLogResponse,
  JudgeQueueGame,
  JudgeTeam,
  JudgeMessage,
  TournamentListItem,
  Tournament,
  TournamentCopyRequest,
  TournamentCopyResponse,
  TournamentImportRequest,
  TournamentImportResponse,
  LeagueRound,
  LeagueRoundUpdateRequest,
  SeedFieldsRequest,
  SeedFieldsResponse,
  GenerateRefereesRequest,
  GenerateRefereesResponse,
  ClearGeneratedRefereesResponse,
  AssignCourtsRequest,
  AssignCourtsResponse,
  TeamPrintData,
  JudgePrintData,
  CourtPreference,
  CreateCourtPreferenceRequest,
  TournamentOverviewResponse,
  CourtsSummaryResponse,
  AuditLog,
  AuditLogParams,
  AnalyticsSummary,
  AnalyticsPageStats,
  AnalyticsFlows,
  AnalyticsJourneys,
  AnalyticsRoutes,
  AnalyticsPatterns,
  SimulateRequest,
  SimulateResponse,
  MediaAsset,
  MediaAssetType,
  PrintAssets,
  HeadJudgeLogEntry,
  HeadJudgeLogStatus,
  HeadJudgeLogCounts,
  AdminUser,
} from '@/types';

/**
 * API client for kyykka-infra backend.
 * 
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401
 * - Type-safe API methods
 */

// Always use relative URL - Vite proxy handles forwarding to Django
const BASE_URL = '/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// ============================================================================
// Token Management
// ============================================================================

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (access: string, refresh: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// ============================================================================
// Request Interceptor - Add JWT token
// ============================================================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// ============================================================================
// Response Interceptor - Handle 401 and refresh token
// ============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<{ access: string }>(
          `${BASE_URL}/v1/token/refresh`,
          { refresh: refreshToken }
        );
        const { access } = response.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
        processQueue(null, access);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Auth API
// ============================================================================

export interface UserProfile {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  telegram: string;
  referee_years: number;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  telegram?: string;
  referee_years?: number;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  telegram?: string;
  referee_years?: number;
}

export const authApi = {
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    const response = await api.post<TokenResponse>('/v1/token', data);
    return response.data;
  },

  refresh: async (refreshToken: string): Promise<{ access: string }> => {
    const response = await api.post<{ access: string }>('/v1/token/refresh', {
      refresh: refreshToken,
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/v1/user');
    return response.data;
  },

  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get<UserProfile>('/v1/user/me');
    return response.data;
  },

  updateProfile: async (data: UpdateProfileRequest): Promise<UserProfile> => {
    const response = await api.patch<UserProfile>('/v1/user/me', data);
    return response.data;
  },
};

// ============================================================================
// Users API
// ============================================================================

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  telegram?: string;
  referee_years?: number;
  is_staff?: boolean;
  is_active?: boolean;
}

export const usersApi = {
  list: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/v1/users');
    return response.data;
  },

  get: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/v1/users/${id}`);
    return response.data;
  },

  create: async (data: CreateUserRequest): Promise<User> => {
    const response = await api.post<User>('/v1/users', data);
    return response.data;
  },

  update: async (id: number, data: UpdateUserRequest): Promise<User> => {
    const response = await api.patch<User>(`/v1/users/${id}`, data);
    return response.data;
  },
};

// ============================================================================
// Teams API
// ============================================================================

export interface TeamListParams {
  league?: string;
  search?: string;
}

export interface TeamImportRequest {
  orders: unknown[];
  clear_existing?: boolean;
}

export interface TeamImportResponse {
  status: string;
  message: string;
  teams_created: number;
  items_created: number;
  yle_count: number;
  pro_count: number;
  variants: Record<string, number>;
}

export interface TeamClearResponse {
  status: string;
  message: string;
  teams_deleted: number;
}

export const teamsApi = {
  list: async (params?: TeamListParams): Promise<Team[]> => {
    const queryParams = new URLSearchParams();
    if (params?.league) queryParams.append('league', params.league);
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    const url = queryString ? `/v1/teams/?${queryString}` : '/v1/teams/';
    const response = await api.get<Team[]>(url);
    return response.data;
  },

  get: async (id: number): Promise<Team> => {
    const response = await api.get<Team>(`/v1/teams/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Team>): Promise<Team> => {
    const response = await api.post<Team>('/v1/teams/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Team>): Promise<Team> => {
    const response = await api.patch<Team>(`/v1/teams/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/teams/${id}/`);
  },

  disqualify: async (id: number): Promise<DisqualifyResponse> => {
    const response = await api.post<DisqualifyResponse>(`/v1/teams/${id}/disqualify`);
    return response.data;
  },

  undisqualify: async (id: number): Promise<UndisqualifyResponse> => {
    const response = await api.post<UndisqualifyResponse>(`/v1/teams/${id}/undisqualify`);
    return response.data;
  },

  import: async (data: TeamImportRequest): Promise<TeamImportResponse> => {
    const response = await api.post<TeamImportResponse>('/v1/teams/import', data);
    return response.data;
  },

  clear: async (league?: string): Promise<TeamClearResponse> => {
    const response = await api.post<TeamClearResponse>('/v1/teams/clear', { league });
    return response.data;
  },
};

// ============================================================================
// Team Comments API
// ============================================================================

export const teamCommentsApi = {
  list: async (teamId: number): Promise<TeamComment[]> => {
    const response = await api.get<TeamComment[]>(`/v1/teams/${teamId}/comments/`);
    return response.data;
  },

  create: async (teamId: number, comment: string): Promise<TeamComment> => {
    const response = await api.post<TeamComment>(`/v1/teams/${teamId}/comments/`, { comment });
    return response.data;
  },

  delete: async (teamId: number, commentId: number): Promise<void> => {
    await api.delete(`/v1/teams/${teamId}/comments/${commentId}/`);
  },
};

// ============================================================================
// Court Preferences API
// ============================================================================

export const courtPrefsApi = {
  list: async (): Promise<CourtPreference[]> => {
    const response = await api.get<CourtPreference[]>('/v1/court-prefs/');
    return response.data;
  },

  create: async (data: CreateCourtPreferenceRequest): Promise<CourtPreference> => {
    const response = await api.post<CourtPreference>('/v1/court-prefs/', data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/court-prefs/${id}/`);
  },
};

// ============================================================================
// Games API
// ============================================================================

export interface UpdateScoresRequest {
  team1_1st_score?: number | null;
  team2_1st_score?: number | null;
  team1_2nd_score?: number | null;
  team2_2nd_score?: number | null;
  team1_ot_score?: number | null;
  team2_ot_score?: number | null;
}

export const gamesApi = {
  list: async (params?: GameListParams): Promise<Game[]> => {
    const queryParams = new URLSearchParams();
    if (params?.league) queryParams.append('league', params.league);
    if (params?.round) queryParams.append('round', String(params.round));
    if (params?.paper) queryParams.append('paper', String(params.paper));
    if (params?.court) queryParams.append('court', String(params.court));
    if (params?.team) queryParams.append('team', String(params.team));
    if (params?.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    const url = queryString ? `/v1/games/?${queryString}` : '/v1/games/';
    const response = await api.get<Game[]>(url);
    return response.data;
  },

  get: async (id: number): Promise<Game> => {
    const response = await api.get<Game>(`/v1/games/${id}/`);
    return response.data;
  },

  updateScores: async (id: number, data: UpdateScoresRequest): Promise<Game> => {
    const response = await api.patch<Game>(`/v1/games/${id}/scores`, data);
    return response.data;
  },

  submitEvent: async (data: EventSubmitRequest): Promise<EventSubmitResponse> => {
    const response = await api.post<EventSubmitResponse>('/v1/events/submit', data);
    return response.data;
  },
};

// ============================================================================
// Blocks API
// ============================================================================

export interface ClearBlockGamesResponse {
  status: string;
  message: string;
  games_cleared: number;
}

export const blocksApi = {
  clearGames: async (paperId: number): Promise<ClearBlockGamesResponse> => {
    const response = await api.post<ClearBlockGamesResponse>(`/v1/blocks/${paperId}/clear-games`);
    return response.data;
  },
};

// ============================================================================
// Referees API
// ============================================================================

export interface CreateShiftRequest {
  date: string;
  start_time: string;
  end_time: string;
  notes?: string;
}

export const refereesApi = {
  list: async (): Promise<Referee[]> => {
    const response = await api.get<Referee[]>('/v1/referees');
    return response.data;
  },

  get: async (id: number): Promise<Referee> => {
    const response = await api.get<Referee>(`/v1/referees/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<Referee>): Promise<Referee> => {
    const response = await api.patch<Referee>(`/v1/referees/${id}`, data);
    return response.data;
  },

  resetPassword: async (id: number): Promise<{ status: string; message: string }> => {
    const response = await api.post<{ status: string; message: string }>(
      `/v1/referees/${id}/reset-password`
    );
    return response.data;
  },

  // Shift management
  getShifts: async (refereeId: number): Promise<RefereeShift[]> => {
    const response = await api.get<RefereeShift[]>(`/v1/referees/${refereeId}/shifts`);
    return response.data;
  },

  createShift: async (refereeId: number, data: CreateShiftRequest): Promise<RefereeShift> => {
    const response = await api.post<RefereeShift>(`/v1/referees/${refereeId}/shifts`, data);
    return response.data;
  },

  updateShift: async (refereeId: number, shiftId: number, data: Partial<CreateShiftRequest>): Promise<RefereeShift> => {
    const response = await api.patch<RefereeShift>(`/v1/referees/${refereeId}/shifts/${shiftId}`, data);
    return response.data;
  },

  deleteShift: async (refereeId: number, shiftId: number): Promise<void> => {
    await api.delete(`/v1/referees/${refereeId}/shifts/${shiftId}`);
  },
};

// ============================================================================
// Shifts API (All shifts across referees)
// ============================================================================

export const shiftsApi = {
  list: async (date?: string): Promise<RefereeShift[]> => {
    const url = date ? `/v1/shifts?date=${date}` : '/v1/shifts';
    const response = await api.get<RefereeShift[]>(url);
    return response.data;
  },
};

// ============================================================================
// Field Groups API
// ============================================================================

export const fieldGroupsApi = {
  list: async (): Promise<FieldGroup[]> => {
    const response = await api.get<FieldGroup[]>('/v1/fieldgroups');
    return response.data;
  },

  get: async (name: string): Promise<FieldGroup> => {
    const response = await api.get<FieldGroup>(`/v1/fieldgroups/${name}`);
    return response.data;
  },

  create: async (data: Partial<FieldGroup>): Promise<FieldGroup> => {
    const response = await api.post<FieldGroup>('/v1/fieldgroups', data);
    return response.data;
  },

  update: async (name: string, data: Partial<FieldGroup>): Promise<FieldGroup> => {
    const response = await api.patch<FieldGroup>(`/v1/fieldgroups/${name}`, data);
    return response.data;
  },

  delete: async (name: string): Promise<void> => {
    await api.delete(`/v1/fieldgroups/${name}`);
  },
};

// ============================================================================
// Fields API
// ============================================================================

export const fieldsApi = {
  list: async (): Promise<Field[]> => {
    const response = await api.get<Field[]>('/v1/fields');
    return response.data;
  },

  get: async (name: string): Promise<Field> => {
    const response = await api.get<Field>(`/v1/fields/${name}`);
    return response.data;
  },

  create: async (data: Partial<Field>): Promise<Field> => {
    const response = await api.post<Field>('/v1/fields', data);
    return response.data;
  },

  update: async (name: string, data: Partial<Field>): Promise<Field> => {
    const response = await api.patch<Field>(`/v1/fields/${name}`, data);
    return response.data;
  },

  delete: async (name: string): Promise<void> => {
    await api.delete(`/v1/fields/${name}`);
  },
};

// ============================================================================
// Admin API
// ============================================================================

export const adminApi = {
  generateTournament: async (data: GenerateTournamentRequest): Promise<GenerateTournamentResponse> => {
    const response = await api.post<GenerateTournamentResponse>('/v1/admin/generate', data);
    return response.data;
  },

  getStats: async (): Promise<TournamentStats> => {
    const response = await api.get<TournamentStats>('/v1/admin/stats');
    return response.data;
  },

  clearTournament: async (): Promise<ClearTournamentResponse> => {
    const response = await api.post<ClearTournamentResponse>('/v1/admin/clear-tournament');
    return response.data;
  },

  clearUsers: async (): Promise<ClearUsersResponse> => {
    const response = await api.post<ClearUsersResponse>('/v1/admin/clear-users');
    return response.data;
  },

  clearAuditLog: async (): Promise<ClearAuditLogResponse> => {
    const response = await api.post<ClearAuditLogResponse>('/v1/admin/clear-audit-log');
    return response.data;
  },

  // Referee management endpoints
  seedFields: async (data: SeedFieldsRequest): Promise<SeedFieldsResponse> => {
    const response = await api.post<SeedFieldsResponse>('/v1/admin/seed-fields', data);
    return response.data;
  },

  clearFields: async (): Promise<ClearFieldsResponse> => {
    const response = await api.post<ClearFieldsResponse>('/v1/admin/clear-fields');
    return response.data;
  },

  generateReferees: async (data: GenerateRefereesRequest): Promise<GenerateRefereesResponse> => {
    const response = await api.post<GenerateRefereesResponse>('/v1/admin/generate-referees', data);
    return response.data;
  },

  clearGeneratedReferees: async (): Promise<ClearGeneratedRefereesResponse> => {
    const response = await api.post<ClearGeneratedRefereesResponse>('/v1/admin/clear-generated-referees');
    return response.data;
  },

  assignCourts: async (data: AssignCourtsRequest): Promise<AssignCourtsResponse> => {
    const response = await api.post<AssignCourtsResponse>('/v1/admin/assign-courts', data);
    return response.data;
  },

  getOverview: async (group: 'games' | 'blocks' | 'rounds' = 'games'): Promise<TournamentOverviewResponse> => {
    const response = await api.get<TournamentOverviewResponse>(`/v1/admin/overview?group=${group}`);
    return response.data;
  },

  getCourtsSummary: async (): Promise<CourtsSummaryResponse> => {
    const response = await api.get<CourtsSummaryResponse>('/v1/admin/courts-summary');
    return response.data;
  },

  simulate: async (params: SimulateRequest): Promise<SimulateResponse> => {
    const response = await api.post<SimulateResponse>('/v1/admin/simulate', params);
    return response.data;
  },
};

// ============================================================================
// Print API
// ============================================================================

export const printApi = {
  getTeams: async (league?: string): Promise<TeamPrintData[]> => {
    const url = league ? `/v1/print/teams?league=${league}` : '/v1/print/teams';
    const response = await api.get<TeamPrintData[]>(url);
    return response.data;
  },

  getTeam: async (id: number): Promise<TeamPrintData> => {
    const response = await api.get<TeamPrintData>(`/v1/print/teams/${id}`);
    return response.data;
  },

  getJudges: async (league?: string): Promise<JudgePrintData[]> => {
    const url = league ? `/v1/print/judges?league=${league}` : '/v1/print/judges';
    const response = await api.get<JudgePrintData[]>(url);
    return response.data;
  },

  getJudge: async (paperId: number): Promise<JudgePrintData> => {
    const response = await api.get<JudgePrintData>(`/v1/print/judges/${paperId}`);
    return response.data;
  },
};

// ============================================================================
// Tournaments API (Timetable Configuration)
// ============================================================================

export const tournamentsApi = {
  list: async (): Promise<TournamentListItem[]> => {
    const response = await api.get<TournamentListItem[]>('/v1/tournaments/');
    return response.data;
  },

  get: async (year: number): Promise<Tournament> => {
    const response = await api.get<Tournament>(`/v1/tournaments/${year}/`);
    return response.data;
  },

  copy: async (year: number, data: TournamentCopyRequest): Promise<TournamentCopyResponse> => {
    const response = await api.post<TournamentCopyResponse>(`/v1/tournaments/${year}/copy/`, data);
    return response.data;
  },

  export: async (year: number): Promise<Blob> => {
    const response = await api.get(`/v1/tournaments/${year}/export/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  import: async (data: TournamentImportRequest): Promise<TournamentImportResponse> => {
    const response = await api.post<TournamentImportResponse>('/v1/tournaments/import/', data);
    return response.data;
  },
};

// ============================================================================
// League Rounds API (Timetable Editing)
// ============================================================================

export const leagueRoundsApi = {
  update: async (id: number, data: LeagueRoundUpdateRequest): Promise<LeagueRound> => {
    const response = await api.patch<LeagueRound>(`/v1/league-rounds/${id}/`, data);
    return response.data;
  },
};

// ============================================================================
// Live API (Public)
// ============================================================================

export const liveApi = {
  getTeams: async (): Promise<Team[]> => {
    const response = await api.get<Team[]>('/live/?teams');
    return response.data;
  },

  getTeam: async (id: number): Promise<Team> => {
    const response = await api.get<Team>(`/live/?team=${id}`);
    return response.data;
  },

  getBlock: async (paperId: number): Promise<BlockData> => {
    const response = await api.get<BlockData>(`/live/?block=${paperId}`);
    return response.data;
  },

  getLeague: async (leagueId: string): Promise<LeagueData> => {
    const response = await api.get<LeagueData>(`/live/?league=${leagueId}`);
    return response.data;
  },

  getField: async (fieldId: number): Promise<LeagueData> => {
    const response = await api.get<LeagueData>(`/live/?field=${fieldId}`);
    return response.data;
  },
};

// ============================================================================
// Judge API (TISSI Interface)
// ============================================================================

export const judgeApi = {
  /**
   * Get games assigned to the current referee.
   */
  getQueue: async (): Promise<JudgeQueueGame[]> => {
    const response = await api.get<JudgeQueueGame[]>('/v1/judge/queue');
    return response.data;
  },

  /**
   * Get teams in a block for autocomplete, with round and league info.
   */
  getTeams: async (paperId: number): Promise<{ teams: JudgeTeam[]; round: number; league_id: string }> => {
    const response = await api.get<{ teams: JudgeTeam[]; round: number; league_id: string }>(`/v1/judge/teams/${paperId}`);
    return response.data;
  },

  /**
   * Get messages for the current referee.
   */
  getMessages: async (): Promise<JudgeMessage[]> => {
    const response = await api.get<JudgeMessage[]>('/v1/judge/messages');
    return response.data;
  },

  /**
   * Mark a message as delivered/read.
   */
  markMessageDelivered: async (messageId: number): Promise<void> => {
    await api.post(`/v1/judge/messages/${messageId}/delivered`);
  },

  /**
   * Submit a game event.
   */
  submitEvent: async (data: EventSubmitRequest): Promise<EventSubmitResponse> => {
    const response = await api.post<EventSubmitResponse>('/v1/events/submit', data);
    return response.data;
  },
};

// ============================================================================
// Audit API
// ============================================================================

export const auditApi = {
  list: async (params?: AuditLogParams): Promise<AuditLog[]> => {
    const queryParams = new URLSearchParams();
    if (params?.game_id) queryParams.append('game_id', String(params.game_id));
    if (params?.team_id) queryParams.append('team_id', String(params.team_id));
    if (params?.block_id) queryParams.append('block_id', String(params.block_id));
    if (params?.court_id) queryParams.append('court_id', String(params.court_id));
    if (params?.referee_id) queryParams.append('referee_id', String(params.referee_id));
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.action) queryParams.append('action', params.action);
    const queryString = queryParams.toString();
    const url = queryString ? `/v1/audit/?${queryString}` : '/v1/audit/';
    const response = await api.get<AuditLog[]>(url);
    return response.data;
  },
};

// ============================================================================
// Analytics API
// ============================================================================

export const analyticsApi = {
  /**
   * Get analytics summary (sessions, visitors, page views).
   */
  summary: async (hours: number = 24): Promise<AnalyticsSummary> => {
    const response = await api.get<AnalyticsSummary>(`/v1/analytics/summary?hours=${hours}`);
    return response.data;
  },

  /**
   * Get page visit statistics.
   */
  pages: async (hours: number = 24, limit: number = 50): Promise<AnalyticsPageStats> => {
    const response = await api.get<AnalyticsPageStats>(
      `/v1/analytics/pages?hours=${hours}&limit=${limit}`
    );
    return response.data;
  },

  /**
   * Get common user navigation flows.
   */
  flows: async (hours: number = 24, limit: number = 20): Promise<AnalyticsFlows> => {
    const response = await api.get<AnalyticsFlows>(
      `/v1/analytics/flows?hours=${hours}&limit=${limit}`
    );
    return response.data;
  },

  /**
   * Get session journeys (full navigation paths).
   */
  journeys: async (hours: number = 24, limit: number = 20): Promise<AnalyticsJourneys> => {
    const response = await api.get<AnalyticsJourneys>(
      `/v1/analytics/journeys?hours=${hours}&limit=${limit}`
    );
    return response.data;
  },

  /**
   * Get exact page routes (no path normalization).
   */
  routes: async (hours: number = 24, limit: number = 100): Promise<AnalyticsRoutes> => {
    const response = await api.get<AnalyticsRoutes>(
      `/v1/analytics/routes?hours=${hours}&limit=${limit}`
    );
    return response.data;
  },

  /**
   * Get aggregated journey patterns.
   */
  patterns: async (hours: number = 24, limit: number = 15): Promise<AnalyticsPatterns> => {
    const response = await api.get<AnalyticsPatterns>(
      `/v1/analytics/patterns?hours=${hours}&limit=${limit}`
    );
    return response.data;
  },
};

// ============================================================================
// App Config API (Translations, etc.)
// ============================================================================

export interface AppConfig<T = unknown> {
  key: string;
  value: T;
  updated_at: string;
  updated_by_username: string | null;
}

export const configApi = {
  /**
   * Get all configs (staff only).
   */
  list: async (): Promise<AppConfig[]> => {
    const response = await api.get<AppConfig[]>('/v1/config/');
    return response.data;
  },

  /**
   * Get a specific config by key.
   * Public keys (translations_*) don't require auth.
   */
  get: async <T = unknown>(key: string): Promise<AppConfig<T>> => {
    const response = await api.get<AppConfig<T>>(`/v1/config/${key}/`);
    return response.data;
  },

  /**
   * Update a config value (staff only).
   */
  update: async <T = unknown>(key: string, value: T): Promise<AppConfig<T>> => {
    const response = await api.put<AppConfig<T>>(`/v1/config/${key}/`, { value });
    return response.data;
  },

  /**
   * Get translation overrides for a locale.
   */
  getTranslations: async (locale: 'fi' | 'en'): Promise<Record<string, string>> => {
    try {
      const config = await configApi.get<Record<string, string>>(`translations_${locale}`);
      return config.value;
    } catch {
      return {}; // Return empty if not configured
    }
  },
};

// ============================================================================
// Restaurant API
// ============================================================================

export interface RestaurantMenuItem {
  id: number;
  nameKey: string;
  price?: string;
  order: number;
}

export interface RestaurantSection {
  id: number;
  title: string;
  order: number;
  items: RestaurantMenuItem[];
}

export interface Restaurant {
  id: number;
  name: string;
  hours: string | { [key: string]: string };
  order: number;
  menu: RestaurantMenuItem[];
  sections: RestaurantSection[];
}

export interface RestaurantListResponse {
  count: number;
  results: Restaurant[];
}

export interface RestaurantCreateRequest {
  name: string;
  hours?: string | { [key: string]: string };
  order?: number;
}

export interface RestaurantSectionCreateRequest {
  restaurant: number;
  title: string;
  order?: number;
}

export interface RestaurantMenuItemCreateRequest {
  restaurant?: number;
  section?: number;
  nameKey: string;
  price?: string;
  order?: number;
}

export const restaurantApi = {
  /**
   * List all restaurants (public).
   * Note: Backend has pagination_class=None, returns array directly.
   */
  list: async (): Promise<Restaurant[]> => {
    const response = await api.get<Restaurant[]>('/v1/restaurants/');
    return response.data;
  },

  /**
   * Get a single restaurant.
   */
  get: async (id: number): Promise<Restaurant> => {
    const response = await api.get<Restaurant>(`/v1/restaurants/${id}/`);
    return response.data;
  },

  /**
   * Create a restaurant (staff only).
   */
  create: async (data: RestaurantCreateRequest): Promise<Restaurant> => {
    const response = await api.post<Restaurant>('/v1/restaurants/', data);
    return response.data;
  },

  /**
   * Update a restaurant (staff only).
   */
  update: async (id: number, data: Partial<RestaurantCreateRequest>): Promise<Restaurant> => {
    const response = await api.patch<Restaurant>(`/v1/restaurants/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a restaurant (staff only).
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/restaurants/${id}/`);
  },

  /**
   * Reorder restaurants (staff only).
   */
  reorder: async (ids: number[]): Promise<void> => {
    await api.post('/v1/restaurants/reorder/', { ids });
  },

  // Section operations (backend has pagination_class=None, returns array directly)
  sections: {
    list: async (restaurantId?: number): Promise<RestaurantSection[]> => {
      const url = restaurantId
        ? `/v1/restaurant-sections/?restaurant=${restaurantId}`
        : '/v1/restaurant-sections/';
      const response = await api.get<RestaurantSection[]>(url);
      return response.data;
    },

    create: async (data: RestaurantSectionCreateRequest): Promise<RestaurantSection> => {
      const response = await api.post<RestaurantSection>('/v1/restaurant-sections/', data);
      return response.data;
    },

    update: async (id: number, data: Partial<RestaurantSectionCreateRequest>): Promise<RestaurantSection> => {
      const response = await api.patch<RestaurantSection>(`/v1/restaurant-sections/${id}/`, data);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/v1/restaurant-sections/${id}/`);
    },

    reorder: async (ids: number[]): Promise<void> => {
      await api.post('/v1/restaurant-sections/reorder/', { ids });
    },
  },

  // Menu item operations (backend has pagination_class=None, returns array directly)
  items: {
    list: async (restaurantId?: number, sectionId?: number): Promise<RestaurantMenuItem[]> => {
      let url = '/v1/restaurant-items/';
      if (sectionId) {
        url += `?section=${sectionId}`;
      } else if (restaurantId) {
        url += `?restaurant=${restaurantId}`;
      }
      const response = await api.get<RestaurantMenuItem[]>(url);
      return response.data;
    },

    create: async (data: RestaurantMenuItemCreateRequest): Promise<RestaurantMenuItem> => {
      // Map nameKey to name_key for the API
      const apiData = {
        ...data,
        name_key: data.nameKey,
      };
      const response = await api.post<RestaurantMenuItem>('/v1/restaurant-items/', apiData);
      return response.data;
    },

    update: async (id: number, data: Partial<RestaurantMenuItemCreateRequest>): Promise<RestaurantMenuItem> => {
      const apiData: Record<string, unknown> = { ...data };
      if (data.nameKey !== undefined) {
        apiData.name_key = data.nameKey;
        delete apiData.nameKey;
      }
      const response = await api.patch<RestaurantMenuItem>(`/v1/restaurant-items/${id}/`, apiData);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/v1/restaurant-items/${id}/`);
    },

    reorder: async (ids: number[]): Promise<void> => {
      await api.post('/v1/restaurant-items/reorder/', { ids });
    },
  },
};

// ============================================================================
// Media API
// ============================================================================

export interface MediaAssetCreateRequest {
  name: string;
  image_type: MediaAssetType;
  file: File;
  active?: boolean;
  order?: number;
}

type MediaListResponse = { count: number; results: MediaAsset[] } | MediaAsset[];

const normalizeListResponse = <T>(data: T[] | { results?: T[] }): T[] => {
  if (Array.isArray(data)) {
    return data;
  }
  return data.results ?? [];
};

export const mediaApi = {
  /**
   * List all media assets (admin only).
   */
  list: async (): Promise<MediaAsset[]> => {
    const response = await api.get<MediaListResponse>('/v1/media/');
    return normalizeListResponse(response.data);
  },

  /**
   * Get a single media asset.
   */
  get: async (id: number): Promise<MediaAsset> => {
    const response = await api.get<MediaAsset>(`/v1/media/${id}/`);
    return response.data;
  },

  /**
   * Create a media asset with file upload (admin only).
   */
  create: async (data: MediaAssetCreateRequest): Promise<MediaAsset> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('image_type', data.image_type);
    formData.append('file', data.file);

    const response = await api.post<MediaAsset>('/v1/media/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Update a media asset (admin only).
   */
  update: async (id: number, data: Partial<Omit<MediaAssetCreateRequest, 'file'>>): Promise<MediaAsset> => {
    const response = await api.patch<MediaAsset>(`/v1/media/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a media asset (admin only).
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/media/${id}/`);
  },

  /**
   * Get active assets grouped by type for prints (public).
   */
  getPrintAssets: async (): Promise<PrintAssets> => {
    const response = await api.get<PrintAssets>('/v1/media/print-assets/');
    return response.data;
  },

  /**
   * Reorder media assets (admin only).
   */
  reorder: async (ids: number[]): Promise<void> => {
    await api.post('/v1/media/reorder/', { ids });
  },
};

// ============================================================================
// Contacts API
// ============================================================================

import type { Contact, ContactRole, PrintContacts } from '@/types';

type ContactListResponse = { count: number; results: Contact[] } | Contact[];

export interface ContactCreateRequest {
  name: string;
  phone: string;
  role: ContactRole;
  order?: number;
  active?: boolean;
}

export const contactsApi = {
  /**
   * List all contacts (admin only).
   */
  list: async (): Promise<Contact[]> => {
    const response = await api.get<ContactListResponse>('/v1/contacts/');
    return normalizeListResponse(response.data);
  },

  /**
   * Create a contact (admin only).
   */
  create: async (data: ContactCreateRequest): Promise<Contact> => {
    const response = await api.post<Contact>('/v1/contacts/', data);
    return response.data;
  },

  /**
   * Update a contact (admin only).
   */
  update: async (id: number, data: Partial<ContactCreateRequest>): Promise<Contact> => {
    const response = await api.patch<Contact>(`/v1/contacts/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a contact (admin only).
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/contacts/${id}/`);
  },

  /**
   * Get active contacts grouped by role for prints (public).
   */
  getPrintContacts: async (): Promise<PrintContacts> => {
    const response = await api.get<PrintContacts>('/v1/contacts/print-contacts/');
    return response.data;
  },
};

// ============================================================================
// Head Judge Log API
// ============================================================================

export interface HeadJudgeLogCreateRequest {
  field?: string | null;  // Field.name is the PK (string)
  description: string;
  status?: HeadJudgeLogStatus;
  assigned_to?: number | null;
}

export interface HeadJudgeLogUpdateRequest {
  field?: string | null;  // Field.name is the PK (string)
  description?: string;
  status?: HeadJudgeLogStatus;
  assigned_to?: number | null;
}

// ============================================================================
// FAQ API
// ============================================================================

export interface FaqItem {
  id: number;
  question: string;
  answer: string;
  order: number;
  active: boolean;
}

export interface FaqItemCreateRequest {
  question: string;
  answer: string;
  order?: number;
  active?: boolean;
}

export const faqApi = {
  /**
   * List all FAQ items (public returns active only, admin returns all).
   */
  list: async (): Promise<FaqItem[]> => {
    const response = await api.get<FaqItem[]>('/v1/faq/');
    return response.data;
  },

  /**
   * Get a single FAQ item (admin only).
   */
  get: async (id: number): Promise<FaqItem> => {
    const response = await api.get<FaqItem>(`/v1/faq/${id}/`);
    return response.data;
  },

  /**
   * Create a FAQ item (admin only).
   */
  create: async (data: FaqItemCreateRequest): Promise<FaqItem> => {
    const response = await api.post<FaqItem>('/v1/faq/', data);
    return response.data;
  },

  /**
   * Update a FAQ item (admin only).
   */
  update: async (id: number, data: Partial<FaqItemCreateRequest>): Promise<FaqItem> => {
    const response = await api.patch<FaqItem>(`/v1/faq/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a FAQ item (admin only).
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/faq/${id}/`);
  },

  /**
   * Reorder FAQ items (admin only).
   */
  reorder: async (ids: number[]): Promise<void> => {
    await api.post('/v1/faq/reorder/', { ids });
  },
};

// ============================================================================
// Head Judge Log API
// ============================================================================

export const headJudgeLogApi = {
  /**
   * List all log entries, optionally filtered by status.
   */
  list: async (status?: HeadJudgeLogStatus): Promise<HeadJudgeLogEntry[]> => {
    const params = status ? { status } : {};
    const response = await api.get<HeadJudgeLogEntry[]>('/v1/head-judge-log/', { params });
    return response.data;
  },

  /**
   * Get a single log entry.
   */
  get: async (id: number): Promise<HeadJudgeLogEntry> => {
    const response = await api.get<HeadJudgeLogEntry>(`/v1/head-judge-log/${id}/`);
    return response.data;
  },

  /**
   * Create a new log entry.
   */
  create: async (data: HeadJudgeLogCreateRequest): Promise<HeadJudgeLogEntry> => {
    const response = await api.post<HeadJudgeLogEntry>('/v1/head-judge-log/', data);
    return response.data;
  },

  /**
   * Update a log entry.
   */
  update: async (id: number, data: HeadJudgeLogUpdateRequest): Promise<HeadJudgeLogEntry> => {
    const response = await api.patch<HeadJudgeLogEntry>(`/v1/head-judge-log/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a log entry.
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/head-judge-log/${id}/`);
  },

  /**
   * Get status counts for filter chips.
   */
  getCounts: async (): Promise<HeadJudgeLogCounts> => {
    const response = await api.get<HeadJudgeLogCounts>('/v1/head-judge-log/counts/');
    return response.data;
  },

  /**
   * Get list of admin users for assignee dropdown.
   */
  getAdminUsers: async (): Promise<AdminUser[]> => {
    const response = await api.get<AdminUser[]>('/v1/head-judge-log/users/');
    return response.data;
  },
};

// ============================================================================
// Venue Map Overlay API
// ============================================================================

import type { VenueMapOverlay, MapConfig } from '@/types';

export interface VenueMapOverlayCreateRequest {
  name: string;
  file: File;
  top_left_lat: string;
  top_left_lng: string;
  delta_x: string;
  delta_y: string;
  rotation: string;
  center_lat: string;
  center_lng: string;
  default_zoom: number;
  active?: boolean;
  order?: number;
}

export const venueMapApi = {
  /**
   * List all venue map overlays (admin only).
   */
  list: async (): Promise<VenueMapOverlay[]> => {
    const response = await api.get<VenueMapOverlay[]>('/v1/venue-maps/');
    return response.data;
  },

  /**
   * Get a single venue map overlay.
   */
  get: async (id: number): Promise<VenueMapOverlay> => {
    const response = await api.get<VenueMapOverlay>(`/v1/venue-maps/${id}/`);
    return response.data;
  },

  /**
   * Create a venue map overlay with file upload (admin only).
   */
  create: async (data: VenueMapOverlayCreateRequest): Promise<VenueMapOverlay> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('file', data.file);
    formData.append('top_left_lat', data.top_left_lat);
    formData.append('top_left_lng', data.top_left_lng);
    formData.append('delta_x', data.delta_x);
    formData.append('delta_y', data.delta_y);
    formData.append('rotation', data.rotation);
    formData.append('center_lat', data.center_lat);
    formData.append('center_lng', data.center_lng);
    formData.append('default_zoom', String(data.default_zoom));
    if (data.active !== undefined) formData.append('active', String(data.active));
    if (data.order !== undefined) formData.append('order', String(data.order));

    const response = await api.post<VenueMapOverlay>('/v1/venue-maps/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Update a venue map overlay (admin only).
   */
  update: async (id: number, data: Partial<Omit<VenueMapOverlayCreateRequest, 'file'>>): Promise<VenueMapOverlay> => {
    const response = await api.patch<VenueMapOverlay>(`/v1/venue-maps/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a venue map overlay (admin only).
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/v1/venue-maps/${id}/`);
  },

  /**
   * Set a venue map overlay as active (admin only).
   */
  activate: async (id: number): Promise<VenueMapOverlay> => {
    const response = await api.post<VenueMapOverlay>(`/v1/venue-maps/${id}/activate/`);
    return response.data;
  },

  /**
   * Reorder venue map overlays (admin only).
   */
  reorder: async (ids: number[]): Promise<void> => {
    await api.post('/v1/venue-maps/reorder/', { ids });
  },

  /**
   * Get the active map configuration (public).
   */
  getConfig: async (): Promise<MapConfig | null> => {
    const response = await api.get<MapConfig>('/v1/map-config/');
    // 204 No Content returns empty object
    if (!response.data || Object.keys(response.data).length === 0) {
      return null;
    }
    return response.data;
  },
};

export { api };

